import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search as SearchIcon, ArrowRight } from "lucide-react";
import { Container, Section, Breadcrumbs } from "../components/Primitives";
import { ProductCard, ProductGridSkeleton } from "../components/ProductCard";
import { getProducts } from "../lib/api";
import { useSEO } from "../lib/seo";

const FALLBACK_LINKS = [
  { to: "/shop/gemstones", label: "Browse certified gemstones" },
  { to: "/shop/rudraksha", label: "Browse rudraksha with X-ray" },
  { to: "/shop/by-planet", label: "Browse by graha" },
  { to: "/shop/by-rashi", label: "Browse by rashi" },
  { to: "/tools/verify-a-certificate", label: "Verify a certificate we issued" },
  { to: "/contact", label: "Ask us — we'll reply within one working day" },
];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") || "").trim();

  useSEO({
    title: q ? `Search: ${q}` : "Search",
    description: q
      ? `Results for "${q}" across the catalogue.`
      : "Search the certified stone, rudraksha and guidance catalogue.",
    canonical: `/search${q ? `?q=${encodeURIComponent(q)}` : ""}`,
  });

  const enabled = q.length > 0;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["search", q],
    queryFn: () => getProducts({ q, limit: 48 }),
    enabled,
  });

  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs
          trail={[
            { label: "Home", to: "/" },
            { label: q ? `Search: ${q}` : "Search" },
          ]}
        />
      </Container>

      <Section>
        <Container>
          {/* Query echo + count */}
          <div className="border-b border-rule pb-6" data-testid="search-header">
            <p className="eyebrow-wide">Site search</p>
            <h1
              className="mt-3 text-[32px] leading-tight tracking-[-0.01em]"
              style={{ fontFamily: "Fraunces, Georgia, serif" }}
            >
              {q ? (
                <>
                  Results for{" "}
                  <span className="italic text-gold" data-testid="search-query-echo">
                    "{q}"
                  </span>
                </>
              ) : (
                "Search the catalogue"
              )}
            </h1>
            {enabled && !isLoading && !isError && (
              <p className="ui mt-3 text-sm text-dim" data-testid="search-count">
                {total === 0
                  ? "No matches yet."
                  : `${total} ${total === 1 ? "match" : "matches"} across the catalogue.`}
              </p>
            )}
          </div>

          {/* Search box — always present so a user can refine */}
          <form
            className="mt-8 flex max-w-xl gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const next = new FormData(e.currentTarget).get("q") || "";
              setParams(next.toString().trim() ? { q: next.toString().trim() } : {});
            }}
            data-testid="search-form"
          >
            <label htmlFor="search-input" className="sr-only">
              Search
            </label>
            <div className="relative flex-1">
              <SearchIcon
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-dim"
                aria-hidden="true"
              />
              <input
                id="search-input"
                name="q"
                defaultValue={q}
                autoFocus
                placeholder="Stone name, mukhi count, report number, graha…"
                className="field w-full pl-9"
                data-testid="search-input"
              />
            </div>
            <button type="submit" className="btn btn-primary" data-testid="search-submit">
              Search
            </button>
          </form>

          {/* States */}
          {!enabled && (
            <div className="mt-14 max-w-2xl" data-testid="search-empty-prompt">
              <p className="text-base leading-relaxed text-dim">
                Type any stone name, a mukhi count, a report number, a graha or a rashi.
                The catalogue is small enough that everything real is one search away.
              </p>
            </div>
          )}

          {enabled && isLoading && (
            <div className="mt-12" data-testid="search-loading">
              <ProductGridSkeleton count={6} />
            </div>
          )}

          {enabled && isError && (
            <div className="mt-12 border border-rule p-6" data-testid="search-error">
              <p className="text-base text-ink">
                Search couldn't reach the catalogue just now. Refresh the page, or write to us and
                we'll answer by return.
              </p>
              <Link to="/contact" className="btn btn-secondary mt-5" data-testid="search-error-contact">
                Contact us
              </Link>
            </div>
          )}

          {/* Results grid */}
          {enabled && !isLoading && !isError && items.length > 0 && (
            <div
              className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              data-testid="search-results"
            >
              {items.map((p, idx) => (
                <ProductCard key={p.id || p.slug || idx} product={p} />
              ))}
            </div>
          )}

          {/* No results — with suggestions + contact */}
          {enabled && !isLoading && !isError && items.length === 0 && (
            <div className="mt-12" data-testid="search-no-results">
              <p className="max-w-2xl text-base leading-relaxed text-dim">
                Nothing in the current inventory matches{" "}
                <span className="italic text-ink">"{q}"</span>. The catalogue is deliberately
                small — every stone here has been cut, photographed and lab-verified by us.
                If a stone you're looking for isn't listed, we may still be able to source it
                for you.
              </p>

              <p className="ui mt-8 text-[11px] uppercase tracking-[0.2em] text-gold">
                Try one of these
              </p>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:max-w-2xl">
                {FALLBACK_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="group inline-flex items-center gap-2 text-[15px] text-ink underline underline-offset-4 hover:text-gold"
                      data-testid={`search-suggestion-${l.to.replace(/\//g, "-")}`}
                    >
                      {l.label}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Container>
      </Section>
    </>
  );
}
